import React, { useState, useEffect } from 'react';
import { availabilityAPI, jobsAPI } from '../../utils/api';
import toastr from 'toastr';
import './InterviewerAvailabilityManager.css';

/**
 * InterviewerAvailabilityManager Component
 * 
 * Allows recruiters to:
 * 1. View and manage interviewer availability
 * 2. Send availability requests to candidates
 * 3. View smart scheduling suggestions
 */
const InterviewerAvailabilityManager = ({ applicationId, jobId, candidateEmail, candidateName, onClose, onAvailabilityRequested, hideHeader = false }) => {
  const [step, setStep] = useState(1); // 1: Select interviewers, 2: View suggestions, 3: Send request
  const [selectedInterviewers, setSelectedInterviewers] = useState([]);
  const [availableInterviewers, setAvailableInterviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestionParams, setSuggestionParams] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 weeks from now
    durationMinutes: 60
  });

  useEffect(() => {
    loadInterviewers();
  }, []);

  const loadInterviewers = async () => {
    try {
      setLoading(true);
      // Search for interviewers (users with interviewer role or frequently used)
      const result = await jobsAPI.searchAzureADUsers('');
      if (result.data && result.data.users) {
        setAvailableInterviewers(result.data.users);
      }
    } catch (error) {
      console.error('Failed to load interviewers:', error);
      toastr.error('Failed to load interviewers');
    } finally {
      setLoading(false);
    }
  };

  const handleInterviewerToggle = (interviewer) => {
    const isSelected = selectedInterviewers.some(i => i.id === interviewer.id);
    if (isSelected) {
      setSelectedInterviewers(selectedInterviewers.filter(i => i.id !== interviewer.id));
    } else {
      setSelectedInterviewers([...selectedInterviewers, interviewer]);
    }
  };

  const handleGetSuggestions = async () => {
    if (selectedInterviewers.length === 0) {
      toastr.error('Please select at least one interviewer');
      return;
    }

    try {
      setLoadingSuggestions(true);
      const interviewerIds = selectedInterviewers.map(i => i.id);
      const result = await availabilityAPI.getSmartSuggestions({
        interviewerIds,
        startDate: suggestionParams.startDate,
        endDate: suggestionParams.endDate,
        durationMinutes: suggestionParams.durationMinutes
      });

      if (result.data && result.data.suggestions) {
        setSmartSuggestions(result.data.suggestions);
        setStep(2);
      } else {
        toastr.warning('No available time slots found for the selected interviewers');
      }
    } catch (error) {
      console.error('Failed to get suggestions:', error);
      toastr.error('Failed to get scheduling suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSendAvailabilityRequest = async () => {
    if (smartSuggestions.length === 0) {
      toastr.error('Please generate suggestions first');
      return;
    }

    try {
      setLoading(true);
      const interviewerIds = selectedInterviewers.map(i => i.id);
      const suggestedSlots = smartSuggestions.map(s => ({
        date: s.date,
        startTime: s.startTime,
        endTime: s.endTime,
        interviewers: s.availableInterviewers
      }));

      const result = await availabilityAPI.sendAvailabilityRequest({
        jobApplicationId: applicationId,
        jobPostingId: jobId,
        interviewerIds,
        suggestedSlots,
        candidateTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      if (result.data && result.data.request) {
        toastr.success('Availability request sent to candidate');
        if (onAvailabilityRequested) {
          onAvailabilityRequested(result.data.request);
        }
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to send availability request:', error);
      toastr.error('Failed to send availability request');
    } finally {
      setLoading(false);
    }
  };

  const filteredInterviewers = availableInterviewers.filter(interviewer => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const name = `${interviewer.displayName || ''} ${interviewer.mail || ''}`.toLowerCase();
    return name.includes(query);
  });

  return (
    <div className="availability-manager">
      {!hideHeader && (
        <div className="availability-manager-header">
          <h2>Interview Availability Coordination</h2>
          {onClose && (
            <button className="close-btn" onClick={onClose}>×</button>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="availability-step">
          <h3>Step 1: Select Interviewers</h3>
          <p className="step-description">Choose one or more interviewers for this interview</p>

          <div className="form-group">
            <label>Search Interviewers</label>
            <input
              type="text"
              placeholder="Type name or email to search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="interviewers-list">
            {loading ? (
              <div className="loading-state">Loading interviewers...</div>
            ) : filteredInterviewers.length === 0 ? (
              <div className="empty-state">No interviewers found</div>
            ) : (
              filteredInterviewers.map(interviewer => {
                const isSelected = selectedInterviewers.some(i => i.id === interviewer.id);
                return (
                  <div
                    key={interviewer.id}
                    className={`interviewer-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleInterviewerToggle(interviewer)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleInterviewerToggle(interviewer)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="interviewer-info">
                      <div className="interviewer-name">{interviewer.displayName || interviewer.mail}</div>
                      <div className="interviewer-email">{interviewer.mail}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {selectedInterviewers.length > 0 && (
            <div className="selected-interviewers">
              <strong>Selected ({selectedInterviewers.length}):</strong>
              {selectedInterviewers.map(i => (
                <span key={i.id} className="selected-tag">{i.displayName || i.mail}</span>
              ))}
            </div>
          )}

          <div className="step-actions">
            <button
              onClick={handleGetSuggestions}
              disabled={selectedInterviewers.length === 0 || loadingSuggestions}
              className="btn-primary"
            >
              {loadingSuggestions ? 'Loading...' : 'Get Available Time Slots'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="availability-step">
          <h3>Step 2: Review Available Time Slots</h3>
          <p className="step-description">These are the suggested time slots based on interviewer availability</p>

          <div className="suggestion-params">
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={suggestionParams.startDate}
                  onChange={(e) => setSuggestionParams({ ...suggestionParams, startDate: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={suggestionParams.endDate}
                  onChange={(e) => setSuggestionParams({ ...suggestionParams, endDate: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Duration (minutes)</label>
                <select
                  value={suggestionParams.durationMinutes}
                  onChange={(e) => setSuggestionParams({ ...suggestionParams, durationMinutes: parseInt(e.target.value) })}
                  className="form-input"
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                  <option value={120}>120 minutes</option>
                </select>
              </div>
            </div>
            <button
              onClick={handleGetSuggestions}
              disabled={loadingSuggestions}
              className="btn-secondary"
            >
              Refresh Suggestions
            </button>
          </div>

          {loadingSuggestions ? (
            <div className="loading-state">Loading suggestions...</div>
          ) : smartSuggestions.length === 0 ? (
            <div className="empty-state">No available time slots found. Try adjusting the date range or duration.</div>
          ) : (
            <div className="suggestions-list">
              {smartSuggestions.slice(0, 10).map((suggestion, index) => (
                <div key={index} className="suggestion-item">
                  <div className="suggestion-date">
                    {new Date(suggestion.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="suggestion-time">
                    {suggestion.startTime} - {suggestion.endTime}
                  </div>
                  <div className="suggestion-interviewers">
                    Available: {suggestion.availableInterviewers.map(i => i.displayName || i.email).join(', ')}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="step-actions">
            <button
              onClick={() => setStep(1)}
              className="btn-secondary"
            >
              Back
            </button>
            <button
              onClick={handleSendAvailabilityRequest}
              disabled={smartSuggestions.length === 0 || loading}
              className="btn-primary"
            >
              {loading ? 'Sending...' : 'Send Availability Request to Candidate'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewerAvailabilityManager;

